import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { NotFoundComponent } from '@teliaee/telia-common-components';
import { Button, Grid, GridColumn, Separator, Spinner } from 'telia-front-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import Config from '../Config';
import GaDataStore, { GaData } from '../stores/GaDataStore';
import LanguageStore from '../stores/LanguageStore';
import RouteStore from '../stores/RouteStore';
import SegmentStore, { SEGMENT_ARI, SEGMENT_ERA, SEGMENT_PRIVATE } from '../stores/SegmentStore';
import API from '../util/ApiUtil';
import { pathnameWithoutLang, urlWithLang } from '../util/NavigationUtil';

interface NotFoundProps {
  searchVariant?: 'light' | 'dark';
  routeStore?: RouteStore;
  segmentStore?: SegmentStore;
  gaDataStore?: GaDataStore;
  languageStore?: LanguageStore;
}

interface PageInAlternativeLanguage {
  isAvailable: boolean;
  path?: string;
}

interface NotFoundState {
  alternativePageFetchStatus: string;
  pageInEstonian: PageInAlternativeLanguage;
  pageInRussian: PageInAlternativeLanguage;
}

@observer
class NotFound extends React.Component<NotFoundProps, NotFoundState> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  constructor(props: NotFoundProps) {
    super(props);
    this.state = {
      alternativePageFetchStatus: 'UNINITIALIZED',
      pageInEstonian: undefined,
      pageInRussian: undefined
    };
  }

  public async componentDidMount() {
    this.props.gaDataStore.push(this.gaEventProps());
    await this.getPageAvailabilityForAlternativeLanguages();
  }

  private async getPageAvailabilityForAlternativeLanguages() {
    const currentPathNameWithoutLang = pathnameWithoutLang(this.props.routeStore.pathname ?? '');
    try {
      this.setState({ alternativePageFetchStatus: 'FETCHING' });
      const pageContent = (
        await API.get('/content/contentByPath', {
          params: { path: currentPathNameWithoutLang },
          timeout: 10 * 1000
        })
      ).data;
      this.setState({
        alternativePageFetchStatus: 'FETCHED',
        pageInEstonian: { isAvailable: pageContent.locales?.includes('et'), path: currentPathNameWithoutLang },
        pageInRussian: { isAvailable: pageContent.locales?.includes('ru_RU'), path: urlWithLang(currentPathNameWithoutLang, 'ru') }
      });
    } catch (_e) {
      this.setState({
        alternativePageFetchStatus: 'ERROR',
        pageInEstonian: { isAvailable: false },
        pageInRussian: { isAvailable: false }
      });
    }
  }

  private handleNavigation = (path: React.MouseEvent<HTMLElement>) => {
    this.props.routeStore.navigate(path);
  };

  private gaEventProps = (): GaData => {
    return {
      event: 'NONIEvent',
      eventCategory: '404 - Page Not Found',
      eventAction: 'Telia Infoveeb',
      eventLabel: window.location.href
    };
  };

  private englishContent = (): JSX.Element => {
    const translate = this.context.translateGlobal;
    return (
      <>
        {!['UNINITIALIZED', 'FETCHING'].includes(this.state.alternativePageFetchStatus) &&
          !this.state.pageInEstonian?.isAvailable &&
          !this.state.pageInRussian?.isAvailable && (
            <p>
              {translate('404.new.goBack')}
              <Button link={true} text={translate('404.new.teliaPage')} url={Config.teliaWebUrl} isUnderlined={true} />
            </p>
          )}
        {this.renderAlternateLanguagePageButtons()}
      </>
    );
  };

  private renderRedirectButtonGridColumn = (redirectLang: 'et' | 'ru') => {
    const translate = this.context.translateGlobal;
    return (
      <GridColumn width={['xs-5', 'sm-4', 'md-3']}>
        <Button
          link={true}
          url={redirectLang === 'et' ? this.state.pageInEstonian.path : this.state.pageInRussian.path}
          external={true}
          isUnderlined={true}
          text={translate(redirectLang === 'et' ? '404.language.estonian' : '404.language.russian')}
          iconRight="#arrow-right"
        />
      </GridColumn>
    );
  };

  private renderAlternateLanguagePageButtons = () => {
    if (['UNINITIALIZED', 'FETCHING'].includes(this.state.alternativePageFetchStatus)) {
      return (
        <>
          <Separator spacing="xxsmall" color="invisible" />
          <Spinner />
        </>
      );
    }
    if (this.state.pageInEstonian?.isAvailable || this.state.pageInRussian?.isAvailable) {
      return (
        <Grid align={['top-xs']}>
          {this.state.pageInEstonian.isAvailable && this.renderRedirectButtonGridColumn('et')}
          {this.state.pageInRussian.isAvailable && this.renderRedirectButtonGridColumn('ru')}
        </Grid>
      );
    }
  };

  public render() {
    const segment = this.props.segmentStore.currentSegment === SEGMENT_PRIVATE ? SEGMENT_ERA : SEGMENT_ARI;
    return (
      <NotFoundComponent
        translations={this.context.translations?.['global']}
        teliaWebUrl={Config.teliaWebUrl}
        currentLang={this.props.languageStore.currentLanguage}
        segment={segment}
        englishContent={this.englishContent()}
        onPathChange={this.handleNavigation}
      />
    );
  }
}

export default inject('routeStore', 'gaDataStore', 'languageStore', 'segmentStore')(NotFound);
